import { isComplexFilterArr } from "@components/conditionalFilterDialog/ConditionalFilterDialog.utils";
import { ISelectItem } from "@components/inputs/select/Select.types";
import { itemsGroupId } from "@components/inputs/valueHelper";
import { IReadOnlyListItem } from "@components/readOnlyList/ReadOnlyListItem";
import { isDateType, isNumericType } from "@evala/odata-metadata";
import { getBoundValue, setNestedValue } from "@odata/Data.utils";
import { IFieldInfo } from "@odata/FieldInfo.utils";
import { EntitySetName } from "@odata/GeneratedEntityTypes";
import { EntitySetWrapper, ODataQueryBuilder } from "@odata/OData";
import { formatValue, isEmptyValue, transformToODataString } from "@odata/OData.utils";
import { formatPercent, isNotDefined } from "@utils/general";
import { memoizeOne } from "@utils/memoizeOne";
import { IOneFetch } from "@utils/oneFetch";
import { capitalize } from "@utils/string";
import i18next from "i18next";

import { EMPTY_VALUE } from "../../../constants";
import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import { ODataFilterFunction, ValueType } from "../../../enums";
import { TRecordType, TValue } from "../../../global.types";
import { ModelEvent } from "../../../model/Model";
import { ITableStorageDefaultCustomData, TableStorage } from "../../../model/TableStorage";
import BindingContext, { createPath, IEntity } from "../../../odata/BindingContext";
import DateType, { getUtcDayjs } from "../../../types/Date";
import { getCachedFilter, IChangedFilter, isAddingNew, isDraftView } from "../../../views/table/TableView.utils";
import { getInfoValue } from "../FieldInfo";
import { IFilterDef } from "../smartFilterBar/SmartFilterBar.types";
import { ISmartODataTableAPI, TSmartODataTableStorage } from "../smartTable/SmartODataTableBase";

export const VALUE_HELPER_MAX_SEARCH_ITEMS = 1000;

export function isValueHelperField(info: IFilterDef | IFieldInfo, storage: TableStorage): boolean {
    // todo: refactor this
    const group = storage.getFilterFieldGroup?.(info.id);
    return (info as IFilterDef).isValueHelp ?? getInfoValue(group, "isValueHelp", { info });
}

export interface IFetchValueHelperItemsOptions {
    context?: IAppContext;
    storage: TableStorage;
    bindingContext: BindingContext;
    fieldInfo: IFieldInfo;
    isEnum: boolean;
    skip?: number;
    filterValue?: string;
}

export const getValueHelpFilterGroupByProps = (bc: BindingContext, info: IFieldInfo): string[] => {
    const props: string[] = [];
    // Check if property has referenced currency property - add to group, so we don't group different currencies
    const property = bc.getProperty();
    const currency = property?.getCurrency();


    if (currency) {
        props.push(`${currency}${BindingContext.ENUM_KEY_PROP}`);
    }

    if (info?.filter?.groupByProperties) {
        props.push(...info.filter.groupByProperties);
    }

    return props;
};

export const createCompositeFilterId = (entity: IEntity, groupByProps: string[]): string => {
    return JSON.stringify(entity);
};

export const parseCompositeFilterId = (id: string): IEntity => {
    return JSON.parse(id);
};

export const filterPercentFormatter = (val: TValue): string => isEmptyValue(val) ? i18next.t("Common:General.Empty").toString() : formatPercent(val);

export async function fetchValueHelperData(opts: IFetchValueHelperItemsOptions, oneFetch: IOneFetch): Promise<{
    data: IEntity[];
    count: number
}> {
    const { bindingContext: bc, fieldInfo: info, storage, isEnum } = opts;
    const isDateFilter = isDateType(bc.getProperty()),
        isNumeric = isNumericType(bc.getProperty()),
        displayName = info?.fieldSettings?.displayName,
        isLocal = bc.isLocal(),
        bindingContext = isLocal ? bc :
            (isEnum ? bc.navigate(BindingContext.ENUM_KEY_PROP) : bc.getNavigationBindingContext(displayName)),
        dataBindingContext = storage.data.bindingContext,
        currentFilterPath = bc.getFullPath(),
        changedFilters = storage.getChangedFilters(),
        applicableFilters = changedFilters.changedFields.filter((info: IChangedFilter) => info.bindingContext.getFullPath() !== currentFilterPath);

    const filters: string[] = [];
    const oDataPrefix = "@odata.";

    let propName = info.id;
    if (displayName) {
        propName += `/${displayName}`;
    }

    const computedPropName = info.filter?.oDataFn ? `${oDataPrefix}${propName}${capitalize(info.filter?.oDataFn)}` : propName;
    const computedPropNameExpression = info.filter?.oDataFn ? `${info.filter.oDataFn}(${propName})` : propName;

    let startsWithFilter: string;
    const startsWithComputedName = `${oDataPrefix}startsWithMatch`;
    if (opts.filterValue) {
        if (isDateFilter) {
            // split by separator based on current date format and filter by single parts of the date
            const currentFormat = DateType.defaultDateFormat;
            // search for separator in current locale format
            const separatorMatch = currentFormat.match(/[^A-Za-z0-9]/);
            const separator = separatorMatch ? separatorMatch[0] : ".";

            // take part orders only, use one char -> mapped to y,m,d
            const dateParts = currentFormat.split(separator).map(part => part.trim()?.[0]?.toLowerCase());

            const parts = opts.filterValue.split(separator);
            const props = { y: "year", m: "month", d: "day" };

            parts.forEach((part, idx) => {
                const partKey = dateParts[idx] as keyof typeof props;
                const partProp = props[partKey];
                const number = Number.parseInt(part, 10);
                if (partProp && !isNaN(number)) {
                    // computed values for year, month, day -> filter by them
                    filters.push(`${oDataPrefix}${partProp} eq ${transformToODataString(number, ValueType.Number)}`);
                }
            });
        } else {
            // Numeric and string filter -> fulltext search
            const transformedProp = isNumeric ? `cast(${computedPropName}, 'Edm.String')` : computedPropName;
            const transformedPropExpression = isNumeric ? `cast(${computedPropNameExpression}, 'Edm.String')` : computedPropName;
            startsWithFilter = `${ODataFilterFunction.Startswith}(${transformedPropExpression},${transformToODataString(opts.filterValue, ValueType.String)})`;
            if (isNumeric) {
                filters.push(startsWithComputedName);
            } else {
                // note the space before filter value to match word starts, not middle parts
                const startWordsFilter = `${ODataFilterFunction.Contains}(${transformedProp},${transformToODataString(` ${opts.filterValue}`, ValueType.String)})`;
                filters.push(`(${startsWithFilter} OR ${startWordsFilter})`);
            }
        }
    }

    const filterQuery = storage.createFilterQuery(applicableFilters);

    if (filterQuery?.isInvalid) {
        // todo: if filter query is not valid, should we keep the previous value or set empty items?
        return { data: [], count: null };
    }

    const isDraft = isDraftView(storage as TSmartODataTableStorage);

    const entitySetWrapper: EntitySetWrapper = storage.oData.fromPath(!isDraft ? dataBindingContext.toString() : storage.data.definition.draftDef.draftEntitySet);
    const query = entitySetWrapper.query() as ODataQueryBuilder;

    if (filterQuery) {
        const baseFilterQuery = !isDraft ? getCachedFilter(filterQuery, storage.data.customSecondaryFilter).query : filterQuery.query;
        if (baseFilterQuery) {
            filters.push(baseFilterQuery);
        }
    }

    if (filters.length) {
        query.filter(filters.map(item => `(${item})`).join(" AND "));
    }

    const groupByProperties: string[] = [
        ...getValueHelpFilterGroupByProps(bindingContext, info)
    ];

    // BE limits order by to 5 different props maximum,
    let orderByLimit = 5;
    if (isDateFilter) {
        // for date filters, group by separately year, month, day to avoid grouping full datetime values
        ["year", "month", "day"].forEach(part => {
            const key = `${oDataPrefix}${part}`;
            query.compute(key, `${part}(${propName})`);
            query.orderBy(key, false);
            --orderByLimit;
        });
    } else if (info.filter?.oDataFn) {
        query.compute(computedPropName, computedPropNameExpression);
    } else if (!isLocal) {
        groupByProperties.push(computedPropName);
    }

    if (startsWithFilter) {
        // order by the
        query.compute(startsWithComputedName, startsWithFilter);
        query.orderBy(startsWithComputedName, false);
        --orderByLimit;
    }

    if (!isLocal && isEnum && BindingContext.ENUM_KEY_PROP !== displayName) {
        groupByProperties.push(`${info.id}/${BindingContext.ENUM_KEY_PROP}`);
    }

    if (info.additionalProperties) {
        groupByProperties.push(...info.additionalProperties?.map(prop => prop.id?.startsWith("/") ? prop.id.substring(1) : `${info.id}/${prop.id}`));
    }

    if (opts.skip) {
        // next page don't need count, just skip first items
        query.skip(opts.skip);
    } else {
        // count is not working now with aggregation/group by in BE
        // query.count();
    }
    query.top(VALUE_HELPER_MAX_SEARCH_ITEMS + 1);

    query.groupBy(...groupByProperties);
    groupByProperties.forEach((groupPropName, index) => {
        if (index < orderByLimit) {
            // probably doesn't matter, but change if needed
            query.orderBy(groupPropName, !isDateFilter);
        }
    });

    const result = await query.fetchData<IEntity[]>(oneFetch.fetch);
    let data = result?.value;
    let count = result._metadata?.count ?? data.length;

    if (isDateFilter) {
        // reconstruct date values from computed year, month, day parts
        for (const entity of data) {
            const { day, month, year } = entity._metadata[""];
            if (year && month && day) {
                // value must be set, otherwise keep it as empty value
                const dateValue = getUtcDayjs(`${year}-${month}-${day}`);
                setNestedValue(dateValue, propName, entity);
            }
        }
    } else if (computedPropName !== propName) {
        // for computed properties, set the original property value to the computed value, so it can be used in select item creation
        const computedPropNameWOprefix = computedPropName.replace(oDataPrefix, "");
        for (const entity of data) {
            const computedValue = entity._metadata?.[""]?.[computedPropNameWOprefix];
            setNestedValue(computedValue, propName, entity);
        }
    }

    if (data.length > VALUE_HELPER_MAX_SEARCH_ITEMS) {
        // BE does not support count with group by yet, use Infinity to signal more items available
        data = data.slice(0, VALUE_HELPER_MAX_SEARCH_ITEMS);
        count = Infinity;
    }

    return {
        data: data ?? [],
        count: count ?? null
    };
}

export function createSelectItem(entity: IEntity, opts: IFetchValueHelperItemsOptions, dataBindingContext?: BindingContext): ISelectItem {
    const { fieldInfo, storage, bindingContext, isEnum } = opts;
    const isDateFilter = isDateType(bindingContext.getProperty());
    const displayName = fieldInfo.fieldSettings?.displayName;

    const rootBc = dataBindingContext ?? storage.data.bindingContext;

    const bc = isEnum ? bindingContext : bindingContext.getNavigationBindingContext(displayName);
    const value = getBoundValue({
        bindingContext: bc,
        data: entity,
        dataBindingContext: rootBc
    });
    let id = isEnum ? value?.[BindingContext.ENUM_KEY_PROP] : value;

    const groupByProps = getValueHelpFilterGroupByProps(bindingContext, fieldInfo);

    if (groupByProps?.length > 0) {
        id = createCompositeFilterId(entity, groupByProps);
    }

    if (isNotDefined(id)) {
        id = EMPTY_VALUE;
    } else if (isDateFilter) {
        // uses Dates as strings, so we can compare ids easily
        id = id.toString();
    }

    return {
        id,
        label: (isEnum && !fieldInfo.formatter) ? value?.[displayName] ?? value?.Name ?? storage.t("Common:General.Empty") : formatValue(value, fieldInfo, {
            entity,
            readonly: true,
            storage,
            placeholder: storage.t("Common:General.Empty"),
            context: opts.context ?? opts?.storage?.context
        }),
        groupId: itemsGroupId,
        additionalData: entity
    };
}

interface IReadOnlyListSmartFilterDef {
    id: string;
    value: TValue;
    filterValue?: string;
    valueType?: ValueType;
}

type IReadOnlyListSmartFilterConf = Pick<IReadOnlyListItem, "isSmartFilterDisabled" | "onSmartFilterClick"> & {
    value: TValue;
};
type TStorageWithReadOnlyListSmartFilter = TableStorage<ISmartODataTableAPI, ITableStorageDefaultCustomData & {
    SmartFilterData: TRecordType<IReadOnlyListSmartFilterConf>
}>;

function handleSmartFilterClick(storage: TableStorage, id: string, shouldRemove: boolean) {
    const tableStorage = storage as unknown as TStorageWithReadOnlyListSmartFilter;
    const { SmartFilterData } = tableStorage.getCustomData();
    const data = SmartFilterData[id];
    const newValue: string[] = [];
    const currentValue = storage.getFilterValueByPath(id);

    if (currentValue && !isComplexFilterArr(currentValue)) {
        (currentValue as string[]).forEach(item => {
            if (item !== data.value) {
                newValue.push(item);
            }
        });
    }

    if (!shouldRemove) {
        newValue.push(data.value as string);
    }

    storage.setFilterValueByPath(id, newValue);
    storage.applyFilters();
    storage.emitter.emit(ModelEvent.FilterChanged);
    storage.refresh();
}

export const getDataForReadOnlyListSmartFilter = memoizeOne(async (storage: TableStorage, filters: string): Promise<IEntity[]> => {

    const query = storage.oData.getEntitySetWrapper(storage.data.bindingContext.getEntitySet().getName() as EntitySetName)
        .query()
        .filter(filters);

    const data = await query.fetchData<IEntity[]>();

    return data.value;
}, (storage, filters) => [storage.data.bindingContext.toString(), filters]);

export async function prepareReadOnlyListSmartFilterProps(storage: TableStorage, props: IReadOnlyListSmartFilterDef[]): Promise<void> {
    const tableStorage = storage as unknown as TStorageWithReadOnlyListSmartFilter;
    const SmartFilterData: TRecordType<IReadOnlyListSmartFilterConf> = {};

    // fetch smart filter data
    const filters = props.map(prop => `${prop.id} eq ${transformToODataString(prop.value, prop.valueType ?? ValueType.String)}`);

    const values = await getDataForReadOnlyListSmartFilter(storage, filters.join(" OR "));

    const basePath = storage.data.bindingContext.getPath();
    props
        .filter((item) => !!storage.data.visibleFilters?.Filters.includes(createPath(basePath, item.id)))
        .forEach((item) => {
            const hasValue = !!values?.find((entity) => entity[item.id] === item.value);
            SmartFilterData[item.id] = {
                value: item.filterValue ?? item.value,
                onSmartFilterClick: handleSmartFilterClick.bind(null, storage),
                isSmartFilterDisabled: !item.value || !hasValue
            };
        });

    tableStorage.setCustomData({ SmartFilterData });
    // refresh whole page
    tableStorage.refresh();
}

export function enhanceHeaderDataWithSmartFilter(storage: TableStorage, data: IReadOnlyListItem[]): IReadOnlyListItem[] {
    const tableStorage = storage as unknown as TStorageWithReadOnlyListSmartFilter;
    const { SmartFilterData } = tableStorage.getCustomData();

    const _hasFilter = (path: string, value: TValue) => {
        const filterVal = storage.getAdditionalFieldData(storage.data.bindingContext.navigate(path), "parsedValue");
        if (Array.isArray(filterVal)) {
            return filterVal.some((item) => item === value);
        }
        return false;
    };

    return data.map(item => {
        if (!item.id || !SmartFilterData?.[item.id]) {
            return item;
        }
        const { value, isSmartFilterDisabled, ...smartFilterProps } = SmartFilterData?.[item.id];
        return {
            ...item,
            ...smartFilterProps,
            isSmartFilterApplied: _hasFilter(item.id, value),
            isSmartFilterDisabled: isSmartFilterDisabled || isAddingNew(storage)
        };
    });
}